import type { FC } from 'react';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Box } from '@mui/material';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import type { Kpi } from 'src/types/planAction';
import { useUserContext } from 'src/components/app/context/UserContext';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';

import { styles } from './index.styles';

export const generateKpiData = (
  kpis: Kpi[],
  selectedKpiId: number,
  currentYear: number,
) => {
  if (!kpis) return {};
  const currentKpi = kpis.find(
    ({ id, year }) => id === selectedKpiId && year === currentYear,
  );

  if (!currentKpi) return {};

  const previousKpi = kpis.find(
    ({ id, year }) => id === selectedKpiId && year === currentYear - 1,
  );
  const upcomingKpi = kpis.find(
    ({ id, year }) => id === selectedKpiId && year === currentYear + 1,
  );
  const kpisPerYear = [previousKpi, currentKpi, upcomingKpi];

  const favs: Kpi['fav'][] = [];
  const labels: number[] = [];
  const hasPlan: Kpi['has_actionplan'][] = [];

  kpisPerYear.forEach((k, index) => {
    favs.push(k ? k.fav : null);
    labels.push(k ? k.year : currentYear - 1 + index);
    hasPlan.push(k ? k.has_actionplan : false);
  });

  return {
    currentKpi,
    favs,
    labels,
    hasPlan,
  };
};

interface KPIHistoryProps {
  kpi: number;
  bigKPI?: boolean;
}

const KPIHistory: FC<KPIHistoryProps> = ({ kpi, bigKPI }) => {
  const { planActionStatus } = usePlanActionContext();
  const { currentSurvey } = useUserContext();
  const { currentKpi, favs, labels, hasPlan } = useMemo(() => {
    if (!currentSurvey || !planActionStatus || !planActionStatus.value) {
      return {};
    }

    return generateKpiData(
      planActionStatus.value.kpis,
      kpi,
      currentSurvey.year,
    );
  }, [kpi, planActionStatus]);

  if (!currentKpi) return <div />;

  return (
    <Box sx={styles.kpiHistory}>
      <Box sx={styles.headline}>{currentKpi.label}</Box>

      <Box sx={styles.workers}>
        {hasPlan.map((plan, index) => (
          <Box key={labels[index]} sx={styles.worker(plan, !!bigKPI)} />
        ))}
      </Box>

      <Box sx={styles.chart}>
        <Line
          width="100%"
          data={{
            labels,
            datasets: [
              {
                data: favs,
                fill: false,
                borderColor: '#FFCC00',
                datalabels: {
                  font: { size: bigKPI ? 28 : 12 },
                },
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              tooltip: {
                enabled: false,
              },
              legend: {
                display: false,
              },
              datalabels: {
                backgroundColor: '#FFFFFF',
              },
            },
            scales: {
              y: {
                axis: 'y',
                display: false,
                grid: {
                  display: false,
                },
                ticks: {
                  display: false,
                },
              },
              x: {
                axis: 'x',
                position: 'top',
                grid: {
                  display: true,
                  tickLength: bigKPI ? 25 : 10,
                },
                border: { display: false },
                ticks: {
                  font: {
                    size: bigKPI ? 32 : undefined,
                  },
                },
              },
              xBottom: {
                axis: 'x',
                position: 'bottom',
                type: 'category',
                grid: {
                  tickLength: bigKPI ? 15 : 10,
                },
                border: { display: true },
                ticks: {
                  display: false,
                },
              },
            },
          }}
          plugins={[ChartDataLabels]}
        />
      </Box>
    </Box>
  );
};

export default KPIHistory;
